import DOMPurify from "dompurify";
import { useMemo } from "react";
import { cn } from "@/lib/utils";

/**
 * Only what the announcement editor can produce. `style` carries the text
 * color; DOMPurify strips scripts, event handlers, and everything else.
 */
const SANITIZE_OPTIONS = {
  ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "s", "u", "span"],
  ALLOWED_ATTR: ["style"],
};

/**
 * Renders an announcement message. Rich bodies (authored in the Settings
 * editor) are sanitized HTML; legacy plain-text bodies keep their line breaks.
 *
 * Rich detection matches the editor's output shape (tiptap wraps everything in
 * `<p>` blocks) rather than "contains a `<`" — a plain-text message like
 * "email <matt@nymbl.app>" must render literally, not be parsed as a tag and
 * stripped by the sanitizer.
 */
export function AnnouncementBody({ html, className }: { html: string; className?: string }) {
  const isHtml = /^\s*<p[\s>]/i.test(html);
  const clean = useMemo(
    () => (isHtml ? DOMPurify.sanitize(html, SANITIZE_OPTIONS) : ""),
    [html, isHtml],
  );

  if (!isHtml) {
    return <p className={cn("m-0 whitespace-pre-wrap", className)}>{html}</p>;
  }
  return (
    <div
      className={cn("[&_p]:my-0.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0", className)}
      // Sanitized above with a tag/attribute allowlist.
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
